import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Pencil, Plus, Trash2, X } from 'lucide-react';
import RecordHistory from './RecordHistory';
import RelatedRecords from './RelatedRecords';
import AllFieldsPanel from '../nativerecords/AllFieldsPanel';
import './NativeItems.css';

// Item record (Native). Tabs: Overview (warehouse panel, incoming supply,
// replenishment, demand), BOM, Where-used, Bins, Cost layers, History.
// Edit covers descriptive fields (name, descriptions, weight, active) —
// native-first save with best-effort NetSuite record patch.

const fmtQty = (n) => (n == null ? '—' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 }));
const fmtMoney = (n, digits = 2) => (n == null ? '—' : `$${Number(n).toFixed(digits)}`);
const fmtDate = (d) => (d ? String(d).slice(0, 10) : '—');
const fmtStamp = (d) => (d ? new Date(d).toLocaleString() : 'unknown');

function NegQty({ value }) {
  const neg = Number(value) < 0;
  return <span className={neg ? 'nit-neg' : ''}>{fmtQty(value)}</span>;
}

// Levels-over-time: a lightweight inline sparkline of end-of-day inventory
// level (reverse-replay of the engine ledger). Flat/empty until native
// movements flow — then we show the friendly note instead of a dead-flat line.
function LevelSeries({ levels }) {
  const series = (levels && levels.series) || [];
  const values = series.map((p) => Number(p.level) || 0);
  const min = Math.min(...values, 0);
  const max = Math.max(...values, 0);
  const hasVariation = series.length >= 2 && max - min > 0.0001;

  if (!hasVariation) {
    return (
      <div className="nit-empty">
        {levels?.note || 'Not enough movement history yet to chart a level series.'}
        {series.length > 0 && (
          <> Current level holds at <strong>{fmtQty(values[values.length - 1])}</strong>.</>
        )}
      </div>
    );
  }

  const W = 640, H = 120, pad = 8;
  const span = max - min || 1;
  const x = (i) => pad + (i * (W - 2 * pad)) / (series.length - 1);
  const y = (v) => H - pad - ((v - min) / span) * (H - 2 * pad);
  const points = values.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');

  return (
    <div>
      <svg className="nit-spark" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label="Inventory level over time">
        {min < 0 && <line x1={pad} x2={W - pad} y1={y(0)} y2={y(0)} className="nit-spark-zero" />}
        <polyline points={points} className="nit-spark-line" />
      </svg>
      <p className="nit-caption">
        End-of-day level, last {levels.windowDays || 90} days ({fmtQty(min)} – {fmtQty(max)}).
        {levels.complete === false && ' Window predates native ledger coverage — earlier points are approximate.'}
      </p>
    </div>
  );
}

// Edit modal — descriptive fields only. Saves via PATCH /api/native-items/:id;
// the server updates the mirror first, then pushes to NetSuite best-effort and
// reports nsSync ('synced' | 'pending' | 'skipped') so we can caption honestly.
function ItemEditModal({ item, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: item.name || '',
    description: item.description || '',
    purchaseDescription: item.purchaseDescription || '',
    weight: item.weight != null ? String(item.weight) : '',
    isActive: !!item.isActive,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const save = async () => {
    if (!form.name.trim()) { setError('Name cannot be blank.'); return; }
    if (form.weight !== '' && !Number.isFinite(Number(form.weight))) { setError('Weight must be a number.'); return; }
    setSaving(true);
    setError(null);
    try {
      const { data } = await axios.patch(`/api/native-items/${encodeURIComponent(item.id)}`, {
        name: form.name,
        description: form.description,
        purchaseDescription: form.purchaseDescription,
        weight: form.weight === '' ? null : Number(form.weight),
        isActive: form.isActive,
      });
      onSaved(data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setSaving(false);
    }
  };

  return (
    <div className="nit-modal-backdrop" onClick={onClose}>
      <div className="nit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="nit-modal-head">
          <h3>Edit item <code>{item.sku}</code></h3>
          <button className="nit-btn nit-btn-ghost" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </div>
        <div className="nit-form">
          <label>
            <span>Name</span>
            <input value={form.name} onChange={set('name')} maxLength={200} />
          </label>
          <label>
            <span>Sales description</span>
            <textarea value={form.description} onChange={set('description')} rows={2} maxLength={999} />
          </label>
          <label>
            <span>Purchase description</span>
            <textarea value={form.purchaseDescription} onChange={set('purchaseDescription')} rows={2} maxLength={999} />
          </label>
          <label>
            <span>Weight {item.weightUnit ? `(${item.weightUnit})` : ''}</span>
            <input value={form.weight} onChange={set('weight')} inputMode="decimal" />
          </label>
          <label className="nit-form-check">
            <input type="checkbox" checked={form.isActive} onChange={set('isActive')} />
            <span>Active</span>
          </label>
          {error && <div className="nit-error">{error}</div>}
          <p className="nit-caption">
            Saves here first, then updates NetSuite. Costing, on-hand, and reorder points are managed by their own flows and are not edited here.
          </p>
          <div className="nit-modal-actions">
            <button className="nit-btn nit-btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
            <button className="nit-btn nit-btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// BOM tab — table + editing for assemblies. Adds/edits/removes go native-first
// then rebuild the NetSuite member sublist server-side; onResult surfaces the
// nsSync notice in the page header.
function BomPanel({ base, item, bom, onResult }) {
  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState({ component: '', bomQuantity: '', componentYield: '100' });
  const [editRow, setEditRow] = useState(null); // { id, bomQuantity, componentYield }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const run = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await fn();
      onResult(data);
      setAdding(false);
      setEditRow(null);
      setAddForm({ component: '', bomQuantity: '', componentYield: '100' });
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  const addComponent = () => run(() => axios.post(`${base}/bom`, {
    component: addForm.component.trim(),
    bomQuantity: Number(addForm.bomQuantity),
    componentYield: Number(addForm.componentYield || 100),
  }));
  const saveRow = () => run(() => axios.patch(`${base}/bom/${editRow.id}`, {
    bomQuantity: Number(editRow.bomQuantity),
    componentYield: Number(editRow.componentYield || 100),
  }));
  const removeRow = (c) => {
    if (!window.confirm(`Remove ${c.component?.sku} from this BOM?`)) return;
    run(() => axios.delete(`${base}/bom/${c.id}`));
  };

  return (
    <div className="nit-card">
      <div className="nit-modal-head" style={{ marginBottom: 0 }}>
        <h3>Bill of materials</h3>
        {item.isAssembly && !adding && (
          <button className="nit-btn" onClick={() => setAdding(true)} disabled={busy}><Plus size={14} /> Add component</button>
        )}
      </div>
      {error && <div className="nit-error" style={{ marginTop: 8 }}>{error}</div>}
      {adding && (
        <div className="nit-bom-add">
          <input placeholder="Component SKU" value={addForm.component} onChange={(e) => setAddForm((f) => ({ ...f, component: e.target.value }))} />
          <input placeholder="BOM qty" inputMode="decimal" value={addForm.bomQuantity} onChange={(e) => setAddForm((f) => ({ ...f, bomQuantity: e.target.value }))} />
          <input placeholder="Yield %" inputMode="decimal" value={addForm.componentYield} onChange={(e) => setAddForm((f) => ({ ...f, componentYield: e.target.value }))} />
          <button className="nit-btn nit-btn-primary" onClick={addComponent} disabled={busy || !addForm.component.trim() || !Number(addForm.bomQuantity)}>{busy ? 'Saving…' : 'Add'}</button>
          <button className="nit-btn nit-btn-ghost" onClick={() => { setAdding(false); setError(null); }} disabled={busy}>Cancel</button>
        </div>
      )}
      {bom.loading && <div className="nit-loading">Loading BOM…</div>}
      {bom.error && <div className="nit-error">{bom.error}</div>}
      {bom.data && bom.data.components.length === 0 && (
        <div className="nit-empty">
          No components — {item.isAssembly ? 'this assembly has no BOM rows yet. Add the first one above.' : 'this item is not an assembly.'}
        </div>
      )}
      {bom.data && bom.data.components.length > 0 && (
        <>
          <div className="nit-table-wrap">
            <table className="nit-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Component</th>
                  <th>Name</th>
                  <th className="r">BOM qty</th>
                  <th className="r">Yield</th>
                  <th className="r">Qty per</th>
                  <th>Unit</th>
                  <th className="r">Component on hand</th>
                  {item.isAssembly && <th />}
                </tr>
              </thead>
              <tbody>
                {bom.data.components.map((c, i) => {
                  const isEditing = editRow && editRow.id === c.id;
                  return (
                    <tr key={c.id || i}>
                      <td className="nit-muted">{c.lineNumber ?? i + 1}</td>
                      <td>
                        {c.component ? (
                          <Link to={`/native-items/${encodeURIComponent(c.component.sku || c.component.id)}`}><code>{c.component.sku}</code></Link>
                        ) : <span className="nit-muted">unknown</span>}
                      </td>
                      <td>{c.component?.name}</td>
                      <td className="r">
                        {isEditing ? (
                          <input className="nit-cell-input" inputMode="decimal" value={editRow.bomQuantity}
                            onChange={(e) => setEditRow((r) => ({ ...r, bomQuantity: e.target.value }))} />
                        ) : fmtQty(c.bomQuantity ?? c.quantityPer)}
                      </td>
                      <td className="r nit-muted">
                        {isEditing ? (
                          <input className="nit-cell-input" inputMode="decimal" value={editRow.componentYield}
                            onChange={(e) => setEditRow((r) => ({ ...r, componentYield: e.target.value }))} />
                        ) : (c.componentYield != null ? `${Math.round(c.componentYield * 100)}%` : '—')}
                      </td>
                      <td className="r">{fmtQty(c.quantityPer)}</td>
                      <td className="nit-muted">{c.unit || '—'}</td>
                      <td className="r"><NegQty value={c.component?.onHand} /></td>
                      {item.isAssembly && (
                        <td className="r nit-bom-actions">
                          {isEditing ? (
                            <>
                              <button className="nit-btn nit-btn-primary" onClick={saveRow} disabled={busy || !Number(editRow.bomQuantity)}>{busy ? '…' : 'Save'}</button>
                              <button className="nit-btn nit-btn-ghost" onClick={() => setEditRow(null)} disabled={busy}>Cancel</button>
                            </>
                          ) : (
                            <>
                              <button className="nit-btn nit-btn-ghost" title="Edit quantities" disabled={busy}
                                onClick={() => setEditRow({ id: c.id, bomQuantity: String(c.bomQuantity ?? c.quantityPer ?? ''), componentYield: String(Math.round((c.componentYield ?? 1) * 100)) })}>
                                <Pencil size={14} />
                              </button>
                              <button className="nit-btn nit-btn-ghost" title="Remove component" disabled={busy} onClick={() => removeRow(c)}>
                                <Trash2 size={14} />
                              </button>
                            </>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="nit-caption">
            {bom.data.total} components · Qty per = BOM qty ÷ yield · edits save natively, then update the NetSuite assembly.
            New components take NetSuite's default unit for that item — check the unit after adding (oils stock lb / consume oz).
          </p>
        </>
      )}
    </div>
  );
}

// Lazy tab loader: fetch once when the tab is first opened. reload() clears
// the cached result so the effect refetches (used after BOM edits).
function useTabData(url, active) {
  const [state, setState] = useState({ data: null, loading: false, error: null });
  useEffect(() => {
    if (!active || state.data || state.loading || state.error) return;
    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));
    axios
      .get(url)
      .then(({ data }) => { if (!cancelled) setState({ data, loading: false, error: null }); })
      .catch((err) => { if (!cancelled) setState({ data: null, loading: false, error: err.response?.data?.error || err.message }); });
    return () => { cancelled = true; };
  }, [active, url, state.data, state.loading, state.error]);
  const reload = useCallback(() => setState({ data: null, loading: false, error: null }), []);
  return { ...state, reload };
}

export default function NativeItemDetail() {
  const { idOrSku } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saveNotice, setSaveNotice] = useState(null);

  const base = `/api/native-items/${encodeURIComponent(idOrSku)}`;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setDetail(null);
    setTab('overview');
    try {
      const { data } = await axios.get(base);
      setDetail(data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [base]);

  useEffect(() => { load(); }, [load]);

  const bom = useTabData(`${base}/bom`, tab === 'bom');
  const used = useTabData(`${base}/where-used`, tab === 'used');
  const bins = useTabData(`${base}/bins`, tab === 'bins');
  const costLayers = useTabData(`${base}/cost-layers`, tab === 'cost');
  const history = useTabData(`${base}/history`, tab === 'history');

  if (loading) return <div className="nit-page"><div className="nit-loading">Loading item…</div></div>;
  if (error) {
    return (
      <div className="nit-page">
        <button className="nit-btn nit-btn-ghost" onClick={() => navigate('/native-items')}><ArrowLeft size={16} /> Back to items</button>
        <div className="nit-error">{error}</div>
      </div>
    );
  }
  if (!detail) return null;

  const { item, replenishment, locations, incoming, demand } = detail;

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'bom', label: 'BOM', count: detail.bomComponentCount },
    { key: 'used', label: 'Where-used', count: detail.whereUsedCount },
    { key: 'bins', label: 'Bins' },
    { key: 'cost', label: 'Cost layers' },
    { key: 'fields', label: 'All fields' },
    { key: 'related', label: 'Related' },
    { key: 'history', label: 'History' },
  ];

  return (
    <div className="nit-page">
      <button className="nit-btn nit-btn-ghost" onClick={() => navigate('/native-items')}><ArrowLeft size={16} /> Back to items</button>

      <div className="nit-item-head" style={{ marginTop: 8 }}>
        <code>{item.sku}</code>
        <h1>{item.name}</h1>
        <span className={`nit-tag ${item.isAssembly ? 'nit-tag-assembly' : ''}`}>{item.itemType || 'item'}</span>
        {!item.isActive && <span className="nit-tag nit-tag-inactive">inactive</span>}
        {item.netsuiteId && <span className="nit-tag">NS {item.netsuiteId}</span>}
        <button className="nit-btn nit-btn-ghost nit-edit-btn" onClick={() => setEditing(true)}>
          <Pencil size={14} /> Edit
        </button>
        <Link to={`/native-items/new?copyFrom=${encodeURIComponent(item.sku || item.id)}`} className="nit-btn" style={{ textDecoration: 'none' }}>
          Make Copy
        </Link>
        <button
          className="nit-btn"
          onClick={async () => {
            const activating = !item.isActive;
            if (!window.confirm(activating ? `Reactivate ${item.sku}?` : `Void (deactivate) ${item.sku}? It stops appearing in pickers; history is untouched.`)) return;
            try {
              const { data } = await axios.patch(`/api/native-items/${encodeURIComponent(item.id)}`, { isActive: activating });
              setSaveNotice(data);
              load();
            } catch (err) {
              setSaveNotice({ nsSync: 'pending', nsError: err.response?.data?.error || err.message });
            }
          }}
        >
          {item.isActive ? 'Void' : 'Reactivate'}
        </button>
      </div>

      {saveNotice && (
        <div className={saveNotice.nsSync === 'pending' ? 'nit-error' : 'nit-notice'} style={{ marginTop: 8 }}>
          {saveNotice.nsSync === 'synced' && 'Saved — NetSuite updated.'}
          {saveNotice.nsSync === 'skipped' && 'Saved natively. (No NetSuite record type for this item — nothing pushed.)'}
          {saveNotice.nsSync === 'pending' && `Saved natively, but the NetSuite update failed: ${saveNotice.nsError || 'unknown error'}. A future sync may revert until re-applied.`}
        </div>
      )}

      {editing && (
        <ItemEditModal
          item={item}
          onClose={() => setEditing(false)}
          onSaved={(result) => { setEditing(false); setSaveNotice(result); load(); }}
        />
      )}

      <div className="nit-tabs">
        {tabs.map((t) => (
          <button key={t.key} className={`nit-tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
            {t.label}
            {t.count != null && <span className="nit-tab-count">{t.count}</span>}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW ─────────────────────────────────────────────────────── */}
      {tab === 'overview' && (
        <>
          <div className="nit-card">
            <h3>Warehouse panel — by location</h3>
            {locations.length === 0 ? (
              <div className="nit-empty">
                No location-level inventory rows in the mirror for this item.
                {Number(item.onHand) ? ` Item-level on-hand is ${fmtQty(item.onHand)}.` : ''}
              </div>
            ) : (
              <div className="nit-table-wrap">
                <table className="nit-table">
                  <thead>
                    <tr>
                      <th>Location</th>
                      <th className="r">On hand</th>
                      <th className="r">Reserved</th>
                      <th className="r">Available</th>
                      <th className="r">Reorder pt</th>
                      <th className="r">Preferred</th>
                      <th className="r">Bins</th>
                    </tr>
                  </thead>
                  <tbody>
                    {locations.map((l) => (
                      <tr key={l.locationId} className={l.belowReorder ? 'nit-below-reorder' : ''}>
                        <td>{l.locationName}</td>
                        <td className="r"><NegQty value={l.onHand} /></td>
                        <td className="r">{fmtQty(l.reserved)}</td>
                        <td className="r">
                          <NegQty value={l.available} />
                          {l.belowReorder && <span className="nit-tag nit-tag-warn" title="Available is below the reorder point">low</span>}
                        </td>
                        <td className="r">
                          {fmtQty(l.reorderPoint)}
                          {l.reorderScope === 'item' && l.reorderPoint != null && <span className="nit-scope" title="Item-level default — no per-location value synced">item</span>}
                        </td>
                        <td className="r nit-muted">{fmtQty(l.preferredStockLevel)}</td>
                        <td className="r nit-muted">{l.binCount > 0 ? l.binCount : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td><strong>All locations</strong></td>
                      <td className="r"><strong><NegQty value={locations.reduce((s, l) => s + (Number(l.onHand) || 0), 0)} /></strong></td>
                      <td className="r"><strong>{fmtQty(locations.reduce((s, l) => s + (Number(l.reserved) || 0), 0))}</strong></td>
                      <td className="r"><strong><NegQty value={locations.reduce((s, l) => s + (Number(l.available) || 0), 0)} /></strong></td>
                      <td className="r" /><td className="r" /><td className="r" />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
            <p className="nit-caption">
              Mirror data, last updated {fmtStamp(detail.locationsSyncedAt || item.mirrorUpdatedAt)}.
              {' '}Reorder point &amp; preferred stock are {replenishment.scope === 'location' ? 'per-location (from NetSuite inventoryitemlocations)' : 'item-level defaults'}; rows tagged <em>item</em> fall back to the item master default.
              {' '}NetSuite item-master on-hand rollup: <strong>{fmtQty(item.onHand)}</strong>
              {Math.abs((locations.reduce((s, l) => s + (Number(l.onHand) || 0), 0)) - (Number(item.onHand) || 0)) > 0.5 &&
                ' (differs from the summed per-location total above — the location rows are the perpetual truth; the item rollup is a separate NetSuite field).'}
              {locations.length > 0 && locations.every((l) => l.binCount === 0) &&
                ' Bin-level quantities are not in the mirror yet — location totals only.'}
            </p>
          </div>

          <div className="nit-grid">
            <div className="nit-card">
              <h3>Incoming supply</h3>
              <div className="nit-stats">
                <div className="nit-stat"><span>Open PO qty</span><strong>{fmtQty(incoming.openPoQty)}</strong></div>
                {item.isAssembly && <div className="nit-stat"><span>Open WO qty</span><strong>{fmtQty(incoming.openWoQty)}</strong></div>}
              </div>
              {incoming.openPos.length > 0 && (
                <table className="nit-table">
                  <thead><tr><th>PO</th><th>Vendor</th><th>Expected</th><th className="r">Remaining</th></tr></thead>
                  <tbody>
                    {incoming.openPos.map((po, i) => (
                      <tr key={`${po.poNumber}-${i}`}>
                        <td><code>{po.poNumber || '—'}</code></td>
                        <td className="nit-muted">{po.vendorName}</td>
                        <td>{fmtDate(po.expectedDeliveryDate)}</td>
                        <td className="r">{fmtQty(po.remainingQty)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {item.isAssembly && incoming.openWos.length > 0 && (
                <table className="nit-table">
                  <thead><tr><th>Work order</th><th>Status</th><th>Ordered</th><th className="r">Remaining</th></tr></thead>
                  <tbody>
                    {incoming.openWos.map((wo, i) => (
                      <tr key={`${wo.workOrderNumber}-${i}`}>
                        <td><Link to={`/workorders/${wo.workOrderNumber}`}><code>WO {wo.workOrderNumber}</code></Link></td>
                        <td className="nit-muted">{wo.status}</td>
                        <td>{fmtDate(wo.orderDate)}</td>
                        <td className="r">{fmtQty(wo.remainingQty)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {incoming.openPos.length === 0 && incoming.openWos.length === 0 && (
                <p className="nit-caption">Nothing on order{item.isAssembly ? ' and no open builds' : ''}.</p>
              )}
              <p className="nit-caption">
                POs synced {fmtStamp(incoming.poSyncedAt)}{item.isAssembly ? ` · WOs synced ${fmtStamp(incoming.woSyncedAt)}` : ''}.
              </p>
            </div>

            <div className="nit-card">
              <h3>Replenishment</h3>
              <div className="nit-facts">
                <span>Reorder / build point</span><strong>{fmtQty(replenishment.reorderPoint)}</strong>
                <span>Preferred stock level</span><strong>{fmtQty(replenishment.preferredStockLevel)}</strong>
                <span>Safety stock</span><strong>{fmtQty(replenishment.safetyStockLevel)}</strong>
                <span>Reorder multiple</span><strong>{fmtQty(replenishment.reorderMultiple)}</strong>
                <span>Lead time</span><strong>{replenishment.leadTimeDays != null ? `${replenishment.leadTimeDays} days` : '—'}</strong>
                <span>Method</span><strong>{replenishment.replenishmentMethod || '—'}</strong>
              </div>
              <p className="nit-caption">{replenishment.scopeNote}</p>
            </div>

            <div className="nit-card">
              <h3>Demand</h3>
              <div className="nit-stats">
                <div className="nit-stat"><span>Sold, last {demand.windowDays} days</span><strong>{fmtQty(demand.trailing90Qty)}</strong></div>
                <div className="nit-stat">
                  <span>Days of supply</span>
                  <strong className={demand.daysOfSupply != null && demand.daysOfSupply < (replenishment.leadTimeDays || 14) ? 'nit-neg' : ''}>
                    {demand.daysOfSupply != null ? `~${fmtQty(demand.daysOfSupply)}` : '—'}
                  </strong>
                </div>
                <div className="nit-stat"><span>Avg selling price</span><strong>{fmtMoney(demand.avgSellingPrice)}</strong></div>
                <div className="nit-stat">
                  <span>Avg margin</span>
                  <strong className={demand.avgMarginPct != null && demand.avgMarginPct < 0 ? 'nit-neg' : ''}>
                    {demand.avgMarginPct != null
                      ? `${demand.avgMarginPct.toFixed(1)}% (${fmtMoney(demand.avgMarginPerUnit)}/unit)`
                      : '—'}
                  </strong>
                </div>
              </div>
              <p className="nit-caption">
                From synced sales-order lines (all channels). Days of supply = total on hand ÷ average daily sales over the window.
                Margin = realized price (blended channels, after line discounts) vs average cost.
                {demand.daysOfSupply == null && ' No sales in the window, so no burn rate to project.'}
              </p>
            </div>

            <div className="nit-card">
              <h3>Item facts</h3>
              <div className="nit-facts">
                {item.description && (<><span>Description</span><strong>{item.description}</strong></>)}
                {item.purchaseDescription && (<><span>Purchase description</span><strong>{item.purchaseDescription}</strong></>)}
                {item.taxSchedule && (<><span>Tax schedule</span><strong>{item.taxSchedule}</strong></>)}
                {item.linkedCaseSingle && (<><span>Linked case/single</span><strong>{item.linkedCaseSingle}</strong></>)}
                <span>Costing</span><strong>{item.costingMethod || '—'}</strong>
                <span>Current cost</span><strong>{fmtMoney(item.currentCost, 4)}</strong>
                <span>Average cost</span><strong>{fmtMoney(item.averageCost, 4)}</strong>
                <span>Last purchase price</span><strong>{fmtMoney(item.lastPurchasePrice, 4)}</strong>
                <span>Total value</span><strong>{fmtMoney(item.totalValue)}</strong>
                {item.vendorName && (<><span>Preferred vendor</span><strong>{item.vendorName}</strong></>)}
                {item.weight != null && (<><span>Weight</span><strong>{item.weight} {item.weightUnit || ''}</strong></>)}
                <span>Created in NS</span><strong>{fmtDate(item.dateCreated)}</strong>
                <span>Last modified in NS</span><strong>{fmtDate(item.lastModifiedDate)}</strong>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── BOM ──────────────────────────────────────────────────────────── */}
      {tab === 'bom' && (
        <BomPanel
          base={base}
          item={item}
          bom={bom}
          onResult={(result) => { setSaveNotice(result); bom.reload(); }}
        />
      )}

      {/* ── WHERE-USED ───────────────────────────────────────────────────── */}
      {tab === 'used' && (
        <div className="nit-card">
          <h3>Where used</h3>
          {used.loading && <div className="nit-loading">Loading where-used…</div>}
          {used.error && <div className="nit-error">{used.error}</div>}
          {used.data && used.data.parents.length === 0 && (
            <div className="nit-empty">This item is not a component of any assembly in the mirror.</div>
          )}
          {used.data && used.data.parents.length > 0 && (
            <>
              <div className="nit-table-wrap">
                <table className="nit-table">
                  <thead>
                    <tr>
                      <th>Assembly</th>
                      <th>Name</th>
                      <th className="r">Qty per build</th>
                      <th>Unit</th>
                      <th className="r">Assembly on hand</th>
                    </tr>
                  </thead>
                  <tbody>
                    {used.data.parents.map((p, i) => (
                      <tr key={i} className={p.parent?.isActive === false ? 'nit-inactive-row' : ''}>
                        <td>
                          {p.parent ? (
                            <Link to={`/native-items/${encodeURIComponent(p.parent.sku || p.parent.id)}`}><code>{p.parent.sku}</code></Link>
                          ) : <span className="nit-muted">unknown</span>}
                        </td>
                        <td>
                          {p.parent?.name}
                          {p.parent?.isActive === false && <span className="nit-tag nit-tag-inactive" style={{ marginLeft: 6 }}>inactive</span>}
                        </td>
                        <td className="r">{fmtQty(p.quantityPer)}</td>
                        <td className="nit-muted">{p.unit || '—'}</td>
                        <td className="r"><NegQty value={p.parent?.onHand} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="nit-caption">
                {used.data.total} assemblies use this item{used.data.truncated ? ' (showing the first 500)' : ''} · BOM mirror refreshed {fmtStamp(used.data.syncedAt)}.
              </p>
            </>
          )}
        </div>
      )}

      {/* ── BINS ─────────────────────────────────────────────────────────── */}
      {tab === 'bins' && (
        <div className="nit-card">
          <h3>Inventory by bin</h3>
          {bins.loading && <div className="nit-loading">Loading bins…</div>}
          {bins.error && <div className="nit-error">{bins.error}</div>}
          {bins.data && bins.data.bins.length === 0 && (
            <div className="nit-empty">No location or bin inventory rows in the mirror for this item.</div>
          )}
          {bins.data && bins.data.bins.length > 0 && (
            <>
              <div className="nit-table-wrap">
                <table className="nit-table">
                  <thead>
                    <tr>
                      <th>Location</th>
                      <th>Bin</th>
                      <th>Zone</th>
                      <th className="r">Quantity</th>
                      <th className="r">Reserved</th>
                      <th className="r">Available</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bins.data.bins.map((b, i) => (
                      <tr key={i}>
                        <td>{b.locationName || '—'}</td>
                        <td>
                          {b.binCode
                            ? <Link to={`/native-items/bin/${encodeURIComponent(b.binCode)}`}><code>{b.binCode}</code></Link>
                            : <span className="nit-muted">no bin (location total)</span>}
                        </td>
                        <td className="nit-muted">{b.zone || '—'}</td>
                        <td className="r"><NegQty value={b.quantity} /></td>
                        <td className="r">{fmtQty(b.reserved)}</td>
                        <td className="r"><NegQty value={b.available} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {bins.data.bins.every((b) => !b.binCode) && (
                <p className="nit-caption">
                  The mirror holds location-level rows only for this item — bin-level quantities are not synced yet.
                </p>
              )}
            </>
          )}
        </div>
      )}

      {/* ── COST LAYERS ──────────────────────────────────────────────────── */}
      {tab === 'cost' && (
        <div className="nit-card">
          <h3>Cost layers {costLayers.data?.item?.costingMethod ? `(${costLayers.data.item.costingMethod})` : ''}</h3>
          {costLayers.loading && <div className="nit-loading">Loading cost layers…</div>}
          {costLayers.error && <div className="nit-error">{costLayers.error}</div>}
          {costLayers.data && costLayers.data.layers.length === 0 && (
            <div className="nit-empty">
              No cost layers for this item yet. Layers are created as the native costing engine receives inventory
              (opening balances, receipts, builds).
            </div>
          )}
          {costLayers.data && costLayers.data.layers.length > 0 && (
            <>
              <div className="nit-table-wrap">
                <table className="nit-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Source</th>
                      <th className="r">Received</th>
                      <th className="r">Remaining</th>
                      <th className="r">Unit cost</th>
                      <th className="r">Remaining value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {costLayers.data.layers.map((l, i) => (
                      <tr key={i} className={Number(l.remainingQuantity) === 0 ? 'nit-inactive-row' : ''}>
                        <td>{fmtDate(l.transactionDate)}</td>
                        <td className="nit-muted">{(l.referenceType || '—').replace(/_/g, ' ')}</td>
                        <td className="r">{fmtQty(l.quantityReceived)}</td>
                        <td className="r">{fmtQty(l.remainingQuantity)}</td>
                        <td className="r">{fmtMoney(l.unitCost, 4)}</td>
                        <td className="r">{fmtMoney((Number(l.remainingQuantity) || 0) * (Number(l.unitCost) || 0))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="nit-caption">{costLayers.data.total} layers, newest first.</p>
            </>
          )}
        </div>
      )}

      {/* ── ALL FIELDS ───────────────────────────────────────────────────── */}
      {/* Full-field capture (tasks #62/#60): every SuiteQL field the item
          sync stores in ns_raw, grouped + label-mapped, promoted mirror
          columns overriding their raw twins. Honest empty state until the
          20260705 migration + backfill land. */}
      {tab === 'fields' && (
        <AllFieldsPanel
          family="item"
          nsRaw={item.nsRaw}
          overrides={{
            taxschedule: item.taxSchedule,
            custitem18: item.unavailableForWorkOrders,
            custitem6: item.toBeSold,
            custitemlinkedcasesingle: item.linkedCaseSingle,
          }}
        />
      )}

      {/* ── RELATED ──────────────────────────────────────────────────────── */}
      {/* One reusable tab (task #55): everything linked to this record per
          the relationship registry — BOM/where-used, PO + SO lines, WO
          components, balances, movement ledger — with counts + previews. */}
      {tab === 'related' && (
        <RelatedRecords recordType="item" id={item.id} />
      )}

      {/* ── HISTORY ──────────────────────────────────────────────────────── */}
      {tab === 'history' && (
        <>
          <div className="nit-card">
            <h3>Levels over time</h3>
            {history.loading && <div className="nit-loading">Loading…</div>}
            {history.data && <LevelSeries levels={history.data.levels} />}
          </div>

          <div className="nit-card">
            <h3>Movement ledger</h3>
            {history.loading && <div className="nit-loading">Loading history…</div>}
            {history.error && <div className="nit-error">{history.error}</div>}
            {history.data && (history.data.ledger || []).length === 0 && (
              <div className="nit-empty">
                No inventory movements recorded for this item yet.
                <br />
                Native movements (receipts, builds, transfers, adjustments) land here as the shadow inventory flows go
                live; synced NetSuite history appears here too once it exists for this item.
              </div>
            )}
            {history.data && (history.data.ledger || []).length > 0 && (
              <>
                <div className="nit-table-wrap">
                  <table className="nit-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Location</th>
                        <th>Bin</th>
                        <th className="r">Qty</th>
                        <th className="r">Unit cost</th>
                        <th>Memo</th>
                        <th>Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.data.ledger.map((m, i) => (
                        <tr key={i}>
                          <td>{fmtDate(m.transactionDate)}</td>
                          <td>{(m.type || '—').replace(/_/g, ' ')}</td>
                          <td className="nit-muted">{m.locationName || m.locationCode || '—'}</td>
                          <td className="nit-muted">{m.binCode || '—'}</td>
                          <td className="r"><NegQty value={m.quantity} /></td>
                          <td className="r nit-muted">{m.unitCost != null ? fmtMoney(m.unitCost, 4) : '—'}</td>
                          <td className="nit-muted">{m.memo || '—'}</td>
                          <td>
                            {m.isEngineRow
                              ? <span className="nit-tag nit-tag-assembly">{m.isShadow ? 'shadow' : 'native'}</span>
                              : <span className="nit-tag">NetSuite</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="nit-caption">
                  Newest first{history.data.ledger.length >= 100 ? ' · showing the 100 most recent' : ''} ·{' '}
                  {history.data.hasEngineData
                    ? 'includes native engine movements'
                    : 'synced NetSuite ledger rows only — native movements begin once the inventory shadow flows go live'}.
                </p>
              </>
            )}
          </div>

          {/* Field changes — record change-history (System Notes replacement).
              Movement history above answers "what moved"; this answers "who
              changed which field, from what to what". */}
          <RecordHistory table="inventory_items" rowId={item.id} />
        </>
      )}
    </div>
  );
}
